import { useState, useEffect } from "react";
import {
  Box, Typography, TextField, InputAdornment, Grid, Card, Button,
  Chip, CircularProgress, IconButton, Tooltip,
  LinearProgress, ToggleButtonGroup, ToggleButton,
} from "@mui/material";
import { ReactComponent as SearchIcon } from "../asserts/logo/Search.svg";
import { ReactComponent as DeleteIcon } from "../asserts/logo/delete.svg";
import { ReactComponent as GridViewIcon } from "../asserts/logo/GridView.svg";
import { ReactComponent as TableViewIcon } from "../asserts/logo/TableViewIcon.svg";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { ReactComponent as WarningIcon } from "../asserts/logo/Danger.svg";
import IntakeBreadcrumb from "../components/intake/IntakeBreadcrumb";
import PortfolioFilterSelect from "../components/portfolio/PortfolioFilterSelect";
import DeleteProjectDialog from "../components/portfolio/DeleteProjectDialog";
import PortfolioTableView from "../components/portfolio/PortfolioTableView";
import {
  CARD_FONT,
  PORTFOLIO_TOOLTIP_SLOT_PROPS,
  PORTFOLIO_RISK_SORT_OPTIONS,
  RISK_COLORS,
  portfolioRiskSortLabel,
  resolveAppStatusDisplay,
  resolveProgressBarColor,
  sortAppsByRiskTier,
} from "../components/portfolio/portfolioShared";
import { ReactComponent as AppsPortfolioIcon } from "../asserts/logo/AppsPortfolio.svg";
import { ReactComponent as AppPortfolioDownIcon } from "../asserts/logo/AppPortfoliodown.svg";
import { ReactComponent as HighAndCriticalRiskIcon } from "../asserts/logo/HighAndCriticalRisk.svg";
import { ReactComponent as HighAndCriticalRiskDownIcon } from "../asserts/logo/HighAndCriticalRisk_Down.svg";
import { ReactComponent as OpenFlagsIcon } from "../asserts/logo/OpenFlags.svg";
import { ReactComponent as OpenFlagsDownIcon } from "../asserts/logo/OpenFlag_Down.svg";
import { ReactComponent as TestPassedIcon } from "../asserts/logo/TestPassed.svg";
import { ReactComponent as TestPassedDownIcon } from "../asserts/logo/TestPassed_Down.svg";
import { ReactComponent as AvgReadinessIcon } from "../asserts/logo/AvgReadiness.svg";
import { ReactComponent as AvgReadinessDownIcon } from "../asserts/logo/AvgReadiness_Down.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { useAuth } from "../context/AuthContext";
import { setProjects, removeProject, setCurrentProject } from "../store/slices/projectSlice";
import { clearIntakeDraft } from "../utils/intakeDraftStorage";
import { clearGate0, resolveProjectRiskLevel } from "../utils/gate0Storage";

const baseUrl = API_BASE_URL;

// Roles that see the full App Portfolio view
const ADMIN_ROLES = ["platform-admin", "enterprise-ai-owner"];

const METRIC_VALUE_COLORS = {
  RISK: "#B82500",
  READY: "#00754E",
  TESTS: "#3895FF",
  FLAGS: "#8A5C00",
};

const CARD_TAG_ACTIVE_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 24,
  px: "8px",
  borderRadius: "12px",
  border: "1px solid #BDD6FF",
  bgcolor: "#F0F6FF",
  fontFamily: CARD_FONT,
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "var(--Primary-50, #005BF0)",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const CARD_TAG_DEFAULT_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 24,
  px: "8px",
  borderRadius: "12px",
  border: "1px solid #E6E6E6",
  bgcolor: "#FFFFFF",
  fontFamily: CARD_FONT,
  fontWeight: 400,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#555555",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const CARD_METRIC_BOX_SX = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 43,
  minWidth: 0,
  border: "1px solid #D9D9D9",
  borderRadius: "8px",
  bgcolor: "#F5F5F5",
  textAlign: "center",
};

function mapProjectToAppCard(project) {
  const g0 = project.gate0_data || project.intake_outcome?.gate0_data || {};
  const riskLevel = resolveProjectRiskLevel(project) || project.risk_level || null;
  const model = project.model || g0.model || "";
  const archetype = g0.archetype || "";
  const extraTags = Array.isArray(project.tags) ? project.tags : [];
  const tagList = [...new Set([model, archetype, ...extraTags].filter(Boolean))];
  const flagCount = Number(project.flag_count || 0);
  const criticalCount = Number(project.critical_count ?? (["CRITICAL", "HIGH"].includes(String(riskLevel || "").toUpperCase()) ? Math.min(flagCount, 2) : 0));
  const warningCount = Number(project.warning_count ?? Math.max(flagCount - criticalCount, 0));

  return {
    ...project,
    subtitle: [g0.domain, g0.intended_use].filter(Boolean).join(" | ")
      || project.department
      || "Department",
    risk_level: riskLevel,
    model,
    tagList,
    risk_score: project.risk_score ?? "--",
    ready_score: project.ready_score ?? "--",
    test_count: project.test_count || 0,
    flag_count: flagCount,
    test_progress: project.test_progress || 0,
    test_total: project.test_total || 0,
    critical_count: criticalCount,
    warning_count: warningCount,
    app_status: project.app_status || "Draft",
  };
}

const FILTER_OUTLINED_ROOT_SX = {
  height: 40,
  minHeight: 40,
  borderRadius: "4px",
  bgcolor: "#FFFFFF",
  fontFamily: "Lato, sans-serif",
  fontSize: 16,
  lineHeight: "24px",
  letterSpacing: 0,
  pl: "12px",
  pr: "12px",
  boxShadow: "0px 2px 3px 1px #55555533 inset",
  "& fieldset": { border: "1px solid #707070" },
  "&:hover fieldset": { border: "1px solid #707070" },
  "&.Mui-focused fieldset": { border: "1px solid #707070" },
};

const FILTER_FIELD_COMPACT_SX = {
  pl: { xs: "12px", md: "8px", xl: "12px" },
  pr: { xs: "12px", md: "8px", xl: "12px" },
  fontSize: { xs: 16, md: 14, xl: 16 },
  lineHeight: { xs: "24px", md: "20px", xl: "24px" },
};

const STAT_GRID_SX = {
  display: "grid",
  width: "100%",
  gridTemplateColumns: {
    xs: "repeat(5, minmax(0, 1fr))",
  },
  gap: { xs: 0.75, sm: 1, md: 1.25 },
  mb: 3,
};

const STAT_CARD_SX = {
  p: { xs: 0.75, sm: 1, md: 1.25 },
  borderRadius: "8px",
  border: "1px solid #E5E7EB",
  bgcolor: "#fff",
  minHeight: { xs: 76, sm: 84, md: 88 },
  minWidth: 0,
  height: "100%",
  overflow: "hidden",
};

const STAT_CARD_LABEL_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 900,
  fontSize: { xs: 9, sm: 10, md: 11, lg: 12, xl: 14 },
  lineHeight: { xs: "12px", sm: "13px", md: "14px", lg: "15px", xl: "22px" },
  letterSpacing: 0,
  color: "#383838",
  textTransform: "uppercase",
  minWidth: 0,
  flex: 1,
  wordBreak: "break-word",
};

const FILTER_BAR_SX = {
  display: "flex",
  alignItems: "center",
  gap: { xs: "8px", md: "6px", xl: "8px" },
  mb: 2.5,
  flexWrap: "wrap",
  minWidth: 0,
};

const VIEW_TOGGLE_ICON_SIZE = 24;

const VIEW_TOGGLE_BASE_SX = {
  p: 0,
  minWidth: 40,
  width: 40,
  height: 40,
  borderRadius: "4px !important",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg path": { fill: "currentColor" },
  "& .MuiTouchRipple-root": { display: "none" },
  "&:hover": { bgcolor: "inherit" },
};

/** Selected view toggle — Figma Default / Hover / Focus / Active / Disabled. */
const VIEW_TOGGLE_SELECTED_SX = {
  "&.Mui-selected": {
    bgcolor: "#005BF0",
    backgroundColor: "#005BF0",
    backgroundImage: "none",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.25)",
  },
  "&.Mui-selected:hover:not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#005BF0",
    backgroundColor: "#005BF0",
    backgroundImage: "none",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.25)",
  },
  "&.Mui-selected:focus:not(:active), &.Mui-selected:focus-visible:not(:active), &.Mui-selected.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#005BF0 !important",
    backgroundColor: "#005BF0 !important",
    backgroundImage: "none !important",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25) !important",
  },
  "&.Mui-selected:hover:focus:not(:active), &.Mui-selected:hover:focus-visible:not(:active), &.Mui-selected:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#005BF0 !important",
    backgroundColor: "#005BF0 !important",
    backgroundImage: "none !important",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25) !important",
  },
  "&.Mui-selected:active, &.Mui-selected:hover:active, &.Mui-selected:focus:active, &.Mui-selected.Mui-focusVisible:active": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage:
      "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0) !important",
    color: "#FFFFFF",
    borderColor: "#005BF0 !important",
    boxShadow: "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5) !important",
  },
  "&.Mui-selected.Mui-disabled": {
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    color: "#BBBBBB",
    border: "1px solid #E6E6E6",
    borderColor: "#E6E6E6 !important",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.25)",
    opacity: 1,
  },
};

const VIEW_TOGGLE_UNSELECTED_FILL =
  "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(0deg, #F5F5F5, #F5F5F5)";
const VIEW_TOGGLE_UNSELECTED_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.08)";

/** Unselected view toggle — Figma Secondary icon button Default / Hover / Focus / Active / Disabled. */
const VIEW_TOGGLE_UNSELECTED_SX = {
  "&:not(.Mui-selected)": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: VIEW_TOGGLE_UNSELECTED_FILL,
    color: "#555555",
    border: "1px solid #BBBBBB !important",
    boxShadow: VIEW_TOGGLE_UNSELECTED_SHADOW,
  },
  "&:not(.Mui-selected):hover:not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.02)), ${VIEW_TOGGLE_UNSELECTED_FILL}`,
    color: "#555555",
    border: "1px solid #BBBBBB !important",
    boxShadow: VIEW_TOGGLE_UNSELECTED_SHADOW,
  },
  "&:not(.Mui-selected):focus:not(:active), &:not(.Mui-selected):focus-visible:not(:active), &:not(.Mui-selected).Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${VIEW_TOGGLE_UNSELECTED_FILL} !important`,
    color: "#555555",
    border: "2px solid #005FCC !important",
    boxShadow: `${VIEW_TOGGLE_UNSELECTED_SHADOW} !important`,
  },
  "&:not(.Mui-selected):active": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${VIEW_TOGGLE_UNSELECTED_FILL} !important`,
    color: "#555555",
    border: "1px solid #BBBBBB !important",
    boxShadow: "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5) !important",
  },
  "&:not(.Mui-selected).Mui-disabled": {
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    color: "#BBBBBB",
    border: "1px solid transparent !important",
    boxShadow: VIEW_TOGGLE_UNSELECTED_SHADOW,
    opacity: 1,
  },
};

const GRID_VIEW_TOGGLE_SX = {
  ...VIEW_TOGGLE_BASE_SX,
  ...VIEW_TOGGLE_UNSELECTED_SX,
  ...VIEW_TOGGLE_SELECTED_SX,
};

const TABLE_VIEW_TOGGLE_SX = {
  ...VIEW_TOGGLE_BASE_SX,
  ...VIEW_TOGGLE_UNSELECTED_SX,
  ...VIEW_TOGGLE_SELECTED_SX,
};

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useAuth();

  const userRoles = user?.roles || [];
  const isAdminRole = userRoles.some((r) => ADMIN_ROLES.includes(r));
  const canCreateProject = userRoles.includes("enterprise-ai-owner");
  const canDeleteProject = userRoles.some((r) =>
    ["enterprise-ai-owner", "platform-admin"].includes(r)
  );

  const projects = useSelector((state) => state.project.projects);

  const [search, setSearch] = useState("");
  const [riskSort, setRiskSort] = useState("critical");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectsToDelete, setProjectsToDelete] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (location.pathname === "/home" || location.pathname === "/projects") {
      fetchProjects();
    }
  }, [location.pathname]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/projects`);
      if (response.data.success) {
        dispatch(setProjects(response.data.data || []));
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = () => {
    navigate("/intake/gate0");
  };

  // Click a project card — route by progress:
  //   no framework saved → Framework config (Gate 0 may be in sessionStorage)
  //   framework saved    → Chat (session resume or create with stored Gate 0)
  const handleCardClick = (app) => {
    dispatch(setCurrentProject({ id: app.id, project_name: app.project_name }));
    // Reviewers without chat: open Summary only
    if (!userRoles.includes("enterprise-ai-owner")) {
      navigate(`/summary/${app.id}`);
      return;
    }
    if (!app.framework_draft) {
      navigate(`/framework/${app.id}`);
    } else {
      navigate(`/chat/${app.id}`);
    }
  };

  const handleDeleteClick = (e, app) => {
    e.stopPropagation();
    setProjectsToDelete([app]);
    setDeleteDialogOpen(true);
  };

  const handleBulkDeleteClick = (selectedApps) => {
    setProjectsToDelete(selectedApps);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectsToDelete.length) return;
    try {
      setDeleting(true);
      for (const project of projectsToDelete) {
        const response = await axios.delete(`${baseUrl}/projects/${project.id}`);
        if (response.data.success) {
          dispatch(removeProject(project.id));
          clearIntakeDraft(project.id);
          clearGate0(project.id);
        }
      }
      setDeleteDialogOpen(false);
      setProjectsToDelete([]);
    } catch (err) {
      console.error("Error deleting project:", err);
      alert(err.response?.data?.error || "Failed to delete project. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
    setProjectsToDelete([]);
  };

  // ── Loading state ──
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // NON-ADMIN ROLES: Show assigned projects only
  // ══════════════════════════════════════════════════════════════════════════
  if (!isAdminRole) {
    const roleName = (userRoles.find((r) => !["offline_access", "uma_authorization", "default-roles-*"].some((x) => r.startsWith(x))) || "team member").replace(/-/g, " ");

    // No projects assigned
    if (projects.length === 0) {
      return (
        <Box sx={{ minHeight: "100%", bgcolor: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center", px: 3 }}>
          <Card elevation={0} sx={{ maxWidth: 480, width: "100%", border: "1px solid #e8e8e8", borderRadius: 3, p: 5, textAlign: "center" }}>
            <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 3 }}>
              <FolderOffOutlinedIcon sx={{ fontSize: 32, color: "#999" }} />
            </Box>
            <Typography variant="h6" fontWeight={700} mb={1}>
              No Projects Assigned
            </Typography>
            <Typography fontSize="14px" color="text.secondary" lineHeight={1.7} mb={1}>
              You haven't been assigned to any project yet.
            </Typography>
            <Typography fontSize="13px" color="text.secondary" lineHeight={1.7}>
              Your <strong style={{ textTransform: "capitalize" }}>{roleName}</strong> role requires a project assignment.
              Please contact your Tenant Admin or Enterprise AI Owner to get assigned.
            </Typography>
            <Typography fontSize="12px" color="#999" mt={3} sx={{ fontStyle: "italic" }}>
              You'll receive an email notification when you're assigned to a project.
            </Typography>
          </Card>
        </Box>
      );
    }

    // Has assigned projects — show them
    return (
      <Box sx={{ p: 3, minHeight: "100%", bgcolor: "#f9fafb" }}>
        <Typography variant="h5" fontWeight={700} mb={0.5}>
          My Projects
        </Typography>
        <Typography fontSize="14px" color="text.secondary" mb={3}>
          Projects assigned to you as <strong style={{ textTransform: "capitalize" }}>{roleName}</strong>
        </Typography>

        <Grid container spacing={2}>
          {projects.map((p) => (
            <Grid item xs={12} md={6} key={p.id}>
              <Card
                onClick={() => handleCardClick(p)}
                elevation={0}
                sx={{
                  borderRadius: "12px", border: "1px solid #e8e8e8", p: 3,
                  cursor: "pointer", transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(0,0,0,0.08)" },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 1.5 }}>
                  <Box>
                    <Typography fontWeight={700} fontSize="16px">{p.project_name}</Typography>
                    <Typography fontSize="12px" color="text.secondary" mt={0.3}>
                      {p.department || "AI Application"}
                    </Typography>
                  </Box>
                  <Chip
                    label={p.workflow_status || "Active"}
                    size="small"
                    sx={{
                      fontWeight: 600, fontSize: "11px",
                      bgcolor: p.workflow_status === "Production" ? "#E8F5E9" : "#F0EBFF",
                      color: p.workflow_status === "Production" ? "#2E7D32" : "#6C47FF",
                    }}
                  />
                </Box>

                <Typography fontSize="13px" color="text.secondary" mb={2}>
                  {p.short_description || "Click to view application details and governance status."}
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography fontSize="11px" color="#999">
                    Created {p.created_at ? new Date(p.created_at).toLocaleDateString() : "recently"}
                  </Typography>
                  <Button
                    size="small" endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    sx={{ textTransform: "none", color: "#6C47FF", fontWeight: 600, fontSize: "12px" }}
                  >
                    View Details
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <AdminAppPortfolioView
      projects={projects}
      search={search}
      setSearch={setSearch}
      riskSort={riskSort}
      setRiskSort={setRiskSort}
      viewMode={viewMode}
      setViewMode={setViewMode}
      canCreateProject={canCreateProject}
      canDeleteProject={canDeleteProject}
      onStartOnboarding={handleStartOnboarding}
      onCardClick={handleCardClick}
      onDeleteClick={handleDeleteClick}
      onBulkDeleteClick={handleBulkDeleteClick}
      deleteDialogOpen={deleteDialogOpen}
      projectsToDelete={projectsToDelete}
      deleting={deleting}
      onDeleteCancel={handleDeleteCancel}
      onDeleteConfirm={handleDeleteConfirm}
    />
  );
}

function AdminAppPortfolioView({
  projects,
  search,
  setSearch,
  riskSort,
  setRiskSort,
  viewMode,
  setViewMode,
  canCreateProject,
  canDeleteProject,
  onStartOnboarding,
  onCardClick,
  onDeleteClick,
  onBulkDeleteClick,
  deleteDialogOpen,
  projectsToDelete,
  deleting,
  onDeleteCancel,
  onDeleteConfirm,
}) {
  const allApps = projects.map(mapProjectToAppCard);

  const filteredApps = sortAppsByRiskTier(
    allApps.filter((p) =>
      (p.project_name || "").toLowerCase().includes(search.toLowerCase())
    ),
    riskSort
  );

  const totalApps = allApps.length;
  const statCards = [
    { label: "APPS IN PORTFOLIO", value: totalApps, sub: `${projects.length} added this month`, color: "#4B82F1" },
    { label: "HIGH AND CRITICAL RISK", value: allApps.filter((p) => ["HIGH", "CRITICAL"].includes(String(p.risk_level || "").toUpperCase())).length, sub: "Need Review", color: "#FF7A59" },
    { label: "OPEN FLAGS", value: allApps.reduce((sum, p) => sum + Number(p.flag_count || 0), 0), sub: `Across ${Math.max(totalApps, 1)} apps`, color: "#F3AE00" },
    { label: "TESTS PASSED", value: "0%", sub: "0 of 0 runs", color: "#22A06B" },
    { label: "AVG. READINESS", value: 0, sub: "0 Below Target", color: "#4B82F1" },
  ];

  return (
    <Box sx={{ bgcolor: "#F8F9FB" }}>
      <IntakeBreadcrumb
        hideTrail
        sticky
        rightContent={(
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              sx={{
                textTransform: "none",
                fontFamily: "Lato, sans-serif",
                borderRadius: "4px",
                fontWeight: 700,
                fontSize: 15,
                lineHeight: "24px",
                letterSpacing: 0,
                width: 126,
                minWidth: 126,
                height: 40,
                minHeight: 40,
                px: "16px",
                py: "8px",
                gap: "4px",
                border: "1px solid #005BF0",
                color: "#005BF0",
                bgcolor: "#FFFFFF",
                boxShadow: "0px 1px 2px 0px #0000000A",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": { border: "1px solid #005BF0", bgcolor: "#EEF4FF", boxShadow: "0px 1px 2px 0px #0000000A" },
              }}
            >
              Export Report
            </Button>
            {canCreateProject && (
              <Button
                variant="contained"
                onClick={onStartOnboarding}
                sx={{
                  textTransform: "none",
                  fontFamily: "Lato, sans-serif",
                  borderRadius: "4px",
                  fontWeight: 700,
                  fontSize: 15,
                  lineHeight: "24px",
                  letterSpacing: 0,
                  width: 122,
                  minWidth: 122,
                  height: 40,
                  minHeight: 40,
                  px: "16px",
                  py: "8px",
                  gap: "4px",
                  color: "#FFFFFF",
                  border: "1px solid transparent",
                  boxShadow: "0px 1px 2px 0px #0000000A",
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundImage: "linear-gradient(0deg, #005BF0, #005BF0), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%)",
                  "&:hover": {
                    boxShadow: "0px 1px 2px 0px #0000000A",
                    backgroundImage: "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, #005BF0, #005BF0), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%)",
                  },
                }}
              >
                Onboard App
              </Button>
            )}
          </Box>
        )}
      />
      <Box sx={{ px: { xs: 1, sm: 1.5, md: 2 }, py: 3, maxWidth: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 700,
            fontSize: 22,
            lineHeight: "normal",
            letterSpacing: 0,
            color: "#383838",
          }}
        >
          App Portfolio
        </Typography>
      </Box>

      {/* Stats Row — always 5 columns; compact padding so they fit on md/lg screens */}
      <Box sx={STAT_GRID_SX}>
        {statCards.map((stat, i) => (
            <Card key={stat.label} elevation={0} sx={STAT_CARD_SX}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 0.375, sm: 0.5, md: 0.75 }, minWidth: 0 }}>
                {i === 0 ? (
                  <AppsPortfolioIcon style={{ width: 18, height: 18, display: "block", flexShrink: 0 }} />
                ) : i === 1 ? (
                  <HighAndCriticalRiskIcon style={{ width: 18, height: 18, display: "block", flexShrink: 0 }} />
                ) : i === 2 ? (
                  <OpenFlagsIcon style={{ width: 18, height: 18, display: "block", flexShrink: 0 }} />
                ) : i === 3 ? (
                  <TestPassedIcon style={{ width: 18, height: 18, display: "block", flexShrink: 0 }} />
                ) : i === 4 ? (
                  <AvgReadinessIcon style={{ width: 18, height: 18, display: "block", flexShrink: 0 }} />
                ) : (
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: stat.color, opacity: 0.2 }} />
                )}
                <Typography sx={STAT_CARD_LABEL_SX}>
                  {stat.label}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 700,
                  fontSize: { xs: 18, sm: 20, md: 24, xl: 32 },
                  lineHeight: { xs: "20px", sm: "22px", md: "26px", xl: "32px" },
                  letterSpacing: 0,
                  color: "#555555",
                  mt: { xs: 0.375, sm: 0.5, md: 0.75 },
                }}
              >
                {stat.value}
              </Typography>
              <Box sx={{ mt: { xs: 0.375, sm: 0.5, md: 0.75 }, display: "inline-flex", alignItems: "center", gap: 0.375, minWidth: 0 }}>
                {i === 0 && <AppPortfolioDownIcon style={{ width: 16, height: 16, display: "block", flexShrink: 0 }} />}
                {i === 1 && <HighAndCriticalRiskDownIcon style={{ width: 16, height: 16, display: "block", flexShrink: 0 }} />}
                {i === 2 && <OpenFlagsDownIcon style={{ width: 16, height: 16, display: "block", flexShrink: 0 }} />}
                {i === 3 && <TestPassedDownIcon style={{ width: 16, height: 16, display: "block", flexShrink: 0 }} />}
                {i === 4 && <AvgReadinessDownIcon style={{ width: 16, height: 16, display: "block", flexShrink: 0 }} />}
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 400,
                    fontSize: { xs: 10, sm: 11, md: 12 },
                    lineHeight: { xs: "14px", md: "18px" },
                    letterSpacing: 0,
                    color: "#555555",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {stat.sub}
                </Typography>
              </Box>
            </Card>
        ))}
      </Box>

      {/* Filter Bar */}
      <Box sx={FILTER_BAR_SX}>
        <TextField
          placeholder="Search by name, team, or model"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          margin="none"
          sx={{
            width: { xs: "100%", md: "auto" },
            flex: {
              xs: "1 1 100%",
              md: "1 1 200px",
              lg: "1 1 260px",
              xl: "0 0 340px",
            },
            minWidth: { md: 160, lg: 200, xl: 340 },
            maxWidth: { md: 260, lg: 300, xl: 340 },
            flexShrink: { md: 1, xl: 0 },
            m: 0,
            "& .MuiOutlinedInput-root": {
              ...FILTER_OUTLINED_ROOT_SX,
              ...FILTER_FIELD_COMPACT_SX,
              gap: "8px",
              color: "#383838",
            },
            "& .MuiOutlinedInput-input": {
              py: "8px",
              px: 0,
              height: 24,
              boxSizing: "content-box",
              overflow: "hidden",
              textOverflow: "ellipsis",
              "&::placeholder": {
                color: "#707070",
                opacity: 1,
                fontFamily: "Lato, sans-serif",
                fontWeight: 400,
                fontSize: { xs: 16, md: 14, xl: 16 },
                lineHeight: { xs: "24px", md: "20px", xl: "24px" },
                letterSpacing: 0,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ margin: 0, maxHeight: "unset" }}>
                <SearchIcon style={{ width: 24, height: 24, flexShrink: 0 }} />
              </InputAdornment>
            ),
          }}
        />
        <PortfolioFilterSelect
          id="portfolio-filter-risk"
          defaultValue="All Risk tiers"
          options={[
            { value: "All Risk tiers", label: "All Risk tiers" },
            { value: "Critical", label: "Critical" },
            { value: "High", label: "High" },
            { value: "Medium", label: "Medium" },
            { value: "Low", label: "Low" },
          ]}
        />
        <PortfolioFilterSelect
          id="portfolio-filter-status"
          defaultValue="All Statuses"
          options={[
            { value: "All Statuses", label: "All Statuses" },
            { value: "Test Running", label: "Test Running" },
            { value: "Passed", label: "Passed" },
            { value: "Flagged", label: "Flagged" },
            { value: "Draft", label: "Draft" },
          ]}
        />
        <PortfolioFilterSelect
          id="portfolio-filter-sort"
          value={riskSort}
          onChange={setRiskSort}
          displayValue={portfolioRiskSortLabel(riskSort)}
          fullWidthOnXs
          options={PORTFOLIO_RISK_SORT_OPTIONS}
        />
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, v) => v && setViewMode(v)}
          size="small"
          sx={{
            ml: { xs: 0, md: "auto" },
            width: { xs: "100%", md: "auto" },
            display: "flex",
            justifyContent: { xs: "flex-end", md: "flex-start" },
            flex: { md: "0 0 auto" },
            flexShrink: 0,
            m: 0,
            gap: { xs: "8px", md: "8px" },
            "& .MuiToggleButtonGroup-grouped": {
              margin: 0,
              "&:not(:first-of-type)": { marginLeft: 0 },
            },
          }}
        >
          <Tooltip title="Grid View" arrow placement="bottom" enterDelay={0} componentsProps={PORTFOLIO_TOOLTIP_SLOT_PROPS}>
            <span>
              <ToggleButton value="grid" sx={GRID_VIEW_TOGGLE_SX}>
                <GridViewIcon style={{ width: VIEW_TOGGLE_ICON_SIZE, height: VIEW_TOGGLE_ICON_SIZE, display: "block" }} />
              </ToggleButton>
            </span>
          </Tooltip>
          <Tooltip title="Table View" arrow placement="bottom" enterDelay={0} componentsProps={PORTFOLIO_TOOLTIP_SLOT_PROPS}>
            <span>
              <ToggleButton value="list" sx={TABLE_VIEW_TOGGLE_SX}>
                <TableViewIcon style={{ width: VIEW_TOGGLE_ICON_SIZE, height: VIEW_TOGGLE_ICON_SIZE, display: "block" }} />
              </ToggleButton>
            </span>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>

      {/* App Cards */}
      {filteredApps.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={6}>
          <Typography color="text.secondary" mb={2}>No applications found.</Typography>
          {canCreateProject && (
            <Button variant="contained" onClick={onStartOnboarding} sx={{ bgcolor: "#6C47FF", textTransform: "none", fontWeight: 600 }}>
              + Onboard your first app
            </Button>
          )}
        </Box>
      ) : viewMode === "grid" ? (
        <Grid container spacing={2}>
          {filteredApps.map((app) => (
            <Grid item xs={12} sm={6} md={4} key={app.id}>
              <AppCard app={app} onClick={() => onCardClick(app)}
                onDelete={canDeleteProject ? (e) => onDeleteClick(e, app) : null} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <PortfolioTableView
          apps={filteredApps}
          onRowClick={onCardClick}
          onDelete={onDeleteClick}
          onBulkDelete={canDeleteProject ? onBulkDeleteClick : null}
          canDelete={canDeleteProject}
        />
      )}

      <DeleteProjectDialog
        open={deleteDialogOpen}
        projectName={projectsToDelete.length === 1 ? projectsToDelete[0]?.project_name : null}
        selectedCount={projectsToDelete.length}
        deleting={deleting}
        onCancel={onDeleteCancel}
        onConfirm={onDeleteConfirm}
      />
      </Box>
    </Box>
  );
}

// ─── App Card ────────────────────────────────────────────────────────────────

function AppStatusIndicator({ status }) {
  if (status.kind === "running") {
    return <TripOriginIcon sx={{ width: 14, height: 14, color: status.color, flexShrink: 0 }} />;
  }
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: status.color,
        flexShrink: 0,
      }}
    />
  );
}

function AppCard({ app, onClick, onDelete }) {
  const riskKey = String(app.risk_level || "MEDIUM").toUpperCase();
  const riskStyle = RISK_COLORS[riskKey] || RISK_COLORS.MEDIUM;
  const status = resolveAppStatusDisplay(app);
  const progressPct = app.test_total > 0 ? Math.min(100, (app.test_progress / app.test_total) * 100) : 0;
  const progressColor = resolveProgressBarColor(app, status);
  const metrics = [
    { l: "RISK", v: app.risk_score, c: METRIC_VALUE_COLORS.RISK },
    { l: "READY", v: app.ready_score, c: METRIC_VALUE_COLORS.READY },
    { l: "TESTS", v: app.test_count, c: METRIC_VALUE_COLORS.TESTS },
    { l: "FLAGS", v: app.flag_count, c: METRIC_VALUE_COLORS.FLAGS },
  ];

  return (
    <Card
      onClick={onClick}
      elevation={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        border: "1px solid #E6E6E6",
        borderBottom: `3px solid ${riskStyle.border}`,
        bgcolor: "#FFFFFF",
        cursor: "pointer",
        transition: "transform 0.2s, box-shadow 0.2s",
        p: { xs: 2, sm: 2.25, md: 2.5 },
        minWidth: 0,
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        },
        "& .app-card-delete": { opacity: 0, transition: "opacity 0.15s" },
        "&:hover .app-card-delete": { opacity: 1 },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 0.75, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: CARD_FONT,
            fontWeight: 900,
            fontSize: 16,
            lineHeight: "100%",
            letterSpacing: 0,
            color: "#555555",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {app.project_name}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
          {onDelete && (
            <Tooltip title="Delete" arrow placement="bottom" enterDelay={0} componentsProps={PORTFOLIO_TOOLTIP_SLOT_PROPS}>
              <span className="app-card-delete" style={{ display: "inline-flex" }}>
                <IconButton
                  size="small"
                  onClick={onDelete}
                  aria-label="Delete project"
                  sx={{
                    p: 0.5,
                    flexShrink: 0,
                    "&:hover": { bgcolor: "rgba(222, 53, 11, 0.08)" },
                  }}
                >
                  <DeleteIcon style={{ width: 16, height: 18, display: "block" }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {app.risk_level && (
            <Box
              sx={{
                px: 1.25,
                py: 0.25,
                borderRadius: "999px",
                bgcolor: riskStyle.bg,
                color: riskStyle.color,
                fontFamily: CARD_FONT,
                fontSize: 11,
                fontWeight: 700,
                lineHeight: "18px",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              {riskKey}
            </Box>
          )}
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: CARD_FONT,
          fontWeight: 400,
          fontSize: 12,
          lineHeight: "100%",
          letterSpacing: 0,
          color: "var(--Grey-50, #707070)",
          mb: 1.25,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {app.subtitle}
      </Typography>

      {app.tagList?.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", mb: 1.5, minWidth: 0 }}>
          {app.tagList.map((tag, i) => (
            <Box
              key={`${tag}-${i}`}
              title={tag}
              sx={i === 0 ? CARD_TAG_ACTIVE_SX : CARD_TAG_DEFAULT_SX}
            >
              {tag}
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: "8px",
          mb: 1.5,
          minWidth: 0,
        }}
      >
        {metrics.map((m) => (
          <Box key={m.l} sx={CARD_METRIC_BOX_SX}>
            <Typography
              sx={{
                fontFamily: CARD_FONT,
                fontSize: 12,
                fontWeight: 700,
                lineHeight: "27px",
                letterSpacing: 0,
                color: m.c,
              }}
            >
              {m.v}
            </Typography>
            <Typography
              sx={{
                fontFamily: CARD_FONT,
                fontSize: 10,
                fontWeight: 400,
                lineHeight: "27px",
                letterSpacing: 0,
                color: "#555555",
                textTransform: "uppercase",
              }}
            >
              {m.l}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 0.75, flexWrap: "wrap" }}>
          <Typography sx={{ fontFamily: CARD_FONT, fontSize: 11, color: "#707070" }}>Test Progress</Typography>
          <Typography sx={{ fontFamily: CARD_FONT, fontSize: 11, color: "#707070", whiteSpace: "nowrap" }}>
            {app.test_progress} of {app.test_total} Complete
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{
            height: 6,
            borderRadius: 999,
            bgcolor: "#F0F0F0",
            "& .MuiLinearProgress-bar": { bgcolor: progressColor, borderRadius: 999 },
          }}
        />
      </Box>

      <Box
        sx={{
          mt: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
          rowGap: 0.75,
          minWidth: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <AppStatusIndicator status={status} />
          <Typography
            sx={{
              fontFamily: CARD_FONT,
              fontSize: 12,
              fontWeight: 500,
              color: status.color,
              whiteSpace: "nowrap",
            }}
          >
            {status.label}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexShrink: 0, flexWrap: "wrap" }}>
          {app.critical_count > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <ErrorOutlineIcon sx={{ width: 14, height: 14, color: "#DE350B" }} />
              <Typography sx={{ fontFamily: CARD_FONT, fontSize: 11, fontWeight: 500, color: "#DE350B", whiteSpace: "nowrap" }}>
                {app.critical_count} critical
              </Typography>
            </Box>
          )}
          {app.warning_count > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <WarningIcon style={{ width: 12, height: 12, display: "block", flexShrink: 0 }} />
              <Typography sx={{ fontFamily: CARD_FONT, fontSize: 11, fontWeight: 500, color: "#8A5C00", whiteSpace: "nowrap" }}>
                {app.warning_count} warnings
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
}
